/**
 * Lesson session state machine.
 *
 * This exists to make one specific competitor failure impossible. A Verbi reviewer who
 * had paid for a year wrote: "After finishing a lesson, it will not proceed to the next
 * lesson, you will be stuck." Others reported audio dying after three lessons. Those are
 * state-machine bugs, so we make the state machine explicit, total, and tested:
 *
 *  - every state has a defined transition for every event (no silent no-ops)
 *  - scoring failure NEVER blocks progress — it resolves to `score: unavailable`
 *  - provider or audio failure degrades to text rather than erroring out
 *  - every turn produces a checkpoint, so a killed app resumes where it was
 *  - `complete` is idempotent
 *
 * See docs/02-user-flows.md §3 for the diagram this implements.
 */

import type { SessionMode } from '../types.js';

export type LessonState =
  | 'idle'
  | 'preparing'
  | 'tutorSpeaking'
  | 'awaitingUser'
  | 'listening'
  | 'scoring'
  | 'turnFeedback'
  | 'degraded'
  | 'paused'
  | 'completing'
  | 'complete'
  | 'abandoned';

export type LessonEvent =
  | { readonly type: 'START'; readonly mode: SessionMode }
  | { readonly type: 'READY' }
  | { readonly type: 'PREPARE_FAILED'; readonly reason: FailureReason; readonly attempt: number }
  | { readonly type: 'TUTOR_DONE' }
  | { readonly type: 'SPEECH_START' }
  | { readonly type: 'SPEECH_END' }
  | { readonly type: 'SILENCE_TIMEOUT' }
  | { readonly type: 'SCORED' }
  | { readonly type: 'SCORE_FAILED'; readonly reason: FailureReason }
  | { readonly type: 'SCORE_TIMEOUT' }
  | { readonly type: 'TEXT_SUBMITTED' }
  | { readonly type: 'NEXT_TURN' }
  | { readonly type: 'LAST_TURN_DONE' }
  | { readonly type: 'REPORT_READY' }
  | { readonly type: 'AUDIO_LOST' }
  | { readonly type: 'AUDIO_RECOVERED' }
  | { readonly type: 'DEGRADE'; readonly reason: FailureReason }
  | { readonly type: 'PAUSE'; readonly reason: PauseReason }
  | { readonly type: 'RESUME' }
  | { readonly type: 'ABANDON' }
  | { readonly type: 'COMPLETE' };

export type FailureReason =
  | 'network'
  | 'provider_unavailable'
  | 'audio_session'
  | 'permission'
  | 'quota'
  | 'timeout'
  | 'unknown';

export type PauseReason = 'backgrounded' | 'call' | 'headphones' | 'user';

/** Maximum connection attempts before we stop retrying and degrade to text. */
export const MAX_PREPARE_ATTEMPTS = 3;

export interface TurnRecord {
  readonly index: number;
  /** `unavailable` records a scoring failure that we refused to let block the lesson. */
  readonly scoreStatus: 'scored' | 'reduced' | 'withheld' | 'unavailable';
  readonly hintUsed: boolean;
  readonly translateUsed: boolean;
  readonly retries: number;
}

export interface LessonContext {
  readonly sessionId: string;
  readonly state: LessonState;
  readonly mode: SessionMode;
  readonly turnIndex: number;
  readonly totalTurns: number;
  readonly turns: readonly TurnRecord[];
  readonly prepareAttempts: number;
  readonly degradeReason: FailureReason | null;
  readonly pauseReason: PauseReason | null;
  /** The state to return to on RESUME. */
  readonly resumeState: LessonState | null;
  /** Set once audio has been rebuilt; a second loss degrades instead of retrying. */
  readonly audioRebuilt: boolean;
  readonly completedAt: number | null;
}

export interface Checkpoint {
  readonly sessionId: string;
  readonly state: LessonState;
  readonly mode: SessionMode;
  readonly turnIndex: number;
  readonly turns: readonly TurnRecord[];
  readonly degradeReason: FailureReason | null;
}

export function initialContext(sessionId: string, totalTurns: number): LessonContext {
  return {
    sessionId,
    state: 'idle',
    mode: 'voice',
    turnIndex: 0,
    totalTurns,
    turns: [],
    prepareAttempts: 0,
    degradeReason: null,
    pauseReason: null,
    resumeState: null,
    audioRebuilt: false,
    completedAt: null,
  };
}

/** States from which the session can be resumed after a pause. */
const RESUMABLE: readonly LessonState[] = [
  'tutorSpeaking',
  'awaitingUser',
  'listening',
  'scoring',
  'turnFeedback',
  'degraded',
];

const TERMINAL: readonly LessonState[] = ['complete', 'abandoned'];

export function isTerminal(state: LessonState): boolean {
  return TERMINAL.includes(state);
}

/**
 * Pure transition function. Unknown (state, event) pairs return the context unchanged
 * *and* are reported via {@link transitionWithDiagnostics} so they can be logged rather
 * than silently swallowed — a silent no-op is exactly how a lesson gets stuck.
 */
export function transition(ctx: LessonContext, event: LessonEvent): LessonContext {
  return transitionWithDiagnostics(ctx, event).context;
}

export interface TransitionResult {
  readonly context: LessonContext;
  /** False when the event was not valid in the current state. */
  readonly handled: boolean;
  /** Side effects the host should perform. Keeps the machine pure. */
  readonly effects: readonly LessonEffect[];
}

export type LessonEffect =
  | { readonly type: 'checkpoint'; readonly checkpoint: Checkpoint }
  | { readonly type: 'rebuildAudio' }
  | { readonly type: 'requestReport' }
  | { readonly type: 'playHint' }
  | { readonly type: 'notifyDegraded'; readonly reason: FailureReason }
  | { readonly type: 'retryPrepare'; readonly attempt: number };

/** Convenience constructor; keeps each handler to its interesting parts. */
function ok(context: LessonContext, effects: readonly LessonEffect[] = []): TransitionResult {
  return { context, handled: true, effects };
}

/** A context change plus its checkpoint, which is the common shape. */
function okCheckpointed(context: LessonContext): TransitionResult {
  return ok(context, [{ type: 'checkpoint', checkpoint: checkpointOf(context) }]);
}

/**
 * Drop to text mode. Used by three different triggers (prepare exhausted, explicit
 * DEGRADE, second audio loss) and must behave identically for all of them.
 */
function degrade(ctx: LessonContext, reason: FailureReason): TransitionResult {
  const next: LessonContext = { ...ctx, state: 'degraded', mode: 'text', degradeReason: reason };
  return ok(next, [
    { type: 'notifyDegraded', reason },
    { type: 'checkpoint', checkpoint: checkpointOf(next) },
  ]);
}

/** Begin the wrap-up. The report is requested as an effect so the machine stays pure. */
function finish(ctx: LessonContext): TransitionResult {
  return ok({ ...ctx, state: 'completing' }, [{ type: 'requestReport' }]);
}

/**
 * Events that are valid from (almost) every live state, handled before the per-state
 * table. Returns null when the event is not one of them.
 */
function globalTransition(ctx: LessonContext, event: LessonEvent): TransitionResult | null {
  if (event.type === 'ABANDON') {
    return okCheckpointed({ ...ctx, state: 'abandoned' });
  }

  if (event.type === 'PAUSE' && ctx.state !== 'paused') {
    return okCheckpointed({
      ...ctx,
      state: 'paused',
      pauseReason: event.reason,
      // A pause from a non-resumable state returns the learner to the turn prompt
      // rather than, say, back into a half-finished recording.
      resumeState: RESUMABLE.includes(ctx.state) ? ctx.state : 'awaitingUser',
    });
  }

  if (event.type === 'DEGRADE' && ctx.state !== 'degraded') {
    return degrade(ctx, event.reason);
  }

  // Audio loss: rebuild once, then degrade. This is the direct fix for the
  // "audio drops out after 3 lessons" failure mode.
  if (event.type === 'AUDIO_LOST' && ctx.mode === 'voice') {
    return ctx.audioRebuilt
      ? degrade(ctx, 'audio_session')
      : ok({ ...ctx, audioRebuilt: true }, [{ type: 'rebuildAudio' }]);
  }

  return null;
}

/** A per-state handler. Returning null means "this event is not valid here". */
type StateHandler = (ctx: LessonContext, event: LessonEvent) => TransitionResult | null;

/**
 * One handler per state. Exhaustive by construction: `Record<LessonState, …>` will not
 * compile if a state is added without a handler, which is what makes the machine total.
 */
const HANDLERS: Record<LessonState, StateHandler> = {
  idle: (ctx, event) =>
    event.type === 'START'
      ? ok({ ...ctx, state: 'preparing', mode: event.mode, prepareAttempts: 0 })
      : null,

  preparing: (ctx, event) => {
    if (event.type === 'READY') return ok({ ...ctx, state: 'tutorSpeaking' });
    if (event.type !== 'PREPARE_FAILED') return null;

    const prepareAttempts = ctx.prepareAttempts + 1;
    return prepareAttempts < MAX_PREPARE_ATTEMPTS
      ? ok({ ...ctx, prepareAttempts }, [{ type: 'retryPrepare', attempt: prepareAttempts }])
      : // Out of retries: degrade rather than fail. The lesson is still playable.
        degrade({ ...ctx, prepareAttempts }, event.reason);
  },

  tutorSpeaking: (ctx, event) => {
    if (event.type === 'TUTOR_DONE') return ok({ ...ctx, state: 'awaitingUser' });
    // Barge-in: the learner speaking over the tutor goes straight to listening.
    if (event.type === 'SPEECH_START') return ok({ ...ctx, state: 'listening' });
    return null;
  },

  awaitingUser: (ctx, event) => {
    if (event.type === 'SPEECH_START') return ok({ ...ctx, state: 'listening' });
    if (event.type === 'TEXT_SUBMITTED') return ok({ ...ctx, state: 'scoring' });
    if (event.type === 'SILENCE_TIMEOUT') return ok(ctx, [{ type: 'playHint' }]);
    return null;
  },

  listening: (ctx, event) => {
    if (event.type === 'SPEECH_END') return ok({ ...ctx, state: 'scoring' });
    if (event.type === 'SILENCE_TIMEOUT') {
      return ok({ ...ctx, state: 'awaitingUser' }, [{ type: 'playHint' }]);
    }
    return null;
  },

  // All three scoring outcomes advance the lesson. A scoring problem is OUR problem;
  // it must never strand the learner mid-lesson.
  scoring: (ctx, event) => {
    if (
      event.type !== 'SCORED' &&
      event.type !== 'SCORE_FAILED' &&
      event.type !== 'SCORE_TIMEOUT'
    ) {
      return null;
    }
    const turn: TurnRecord = {
      index: ctx.turnIndex,
      scoreStatus: event.type === 'SCORED' ? 'scored' : 'unavailable',
      hintUsed: false,
      translateUsed: false,
      retries: 0,
    };
    return okCheckpointed({ ...ctx, state: 'turnFeedback', turns: [...ctx.turns, turn] });
  },

  turnFeedback: (ctx, event) => {
    if (event.type === 'LAST_TURN_DONE') return finish(ctx);
    if (event.type !== 'NEXT_TURN') return null;

    const turnIndex = ctx.turnIndex + 1;
    if (turnIndex >= ctx.totalTurns) return finish(ctx);
    return okCheckpointed({
      ...ctx,
      state: ctx.mode === 'text' ? 'awaitingUser' : 'tutorSpeaking',
      turnIndex,
    });
  },

  // A text-mode lesson continues to completion rather than dead-ending.
  degraded: (ctx, event) => {
    if (event.type === 'TEXT_SUBMITTED') return ok({ ...ctx, state: 'scoring' });
    if (event.type === 'AUDIO_RECOVERED') {
      return ok({ ...ctx, state: 'awaitingUser', mode: 'voice', degradeReason: null });
    }
    if (event.type !== 'NEXT_TURN') return null;

    const turnIndex = ctx.turnIndex + 1;
    return turnIndex >= ctx.totalTurns ? finish(ctx) : ok({ ...ctx, turnIndex });
  },

  paused: (ctx, event) =>
    event.type === 'RESUME'
      ? ok({
          ...ctx,
          state: ctx.resumeState ?? 'awaitingUser',
          pauseReason: null,
          resumeState: null,
        })
      : null,

  completing: (ctx, event) =>
    event.type === 'REPORT_READY' || event.type === 'COMPLETE'
      ? okCheckpointed({ ...ctx, state: 'complete', completedAt: Date.now() })
      : null,

  // Terminal states are handled before the table is consulted.
  complete: () => null,
  abandoned: () => null,
};

export function transitionWithDiagnostics(
  ctx: LessonContext,
  event: LessonEvent,
): TransitionResult {
  const unhandled: TransitionResult = { context: ctx, handled: false, effects: [] };

  // Terminal states accept nothing except an idempotent re-COMPLETE.
  if (isTerminal(ctx.state)) {
    // Idempotency: re-posting completion must be a no-op, never a double count.
    return event.type === 'COMPLETE' && ctx.state === 'complete' ? ok(ctx) : unhandled;
  }

  return globalTransition(ctx, event) ?? HANDLERS[ctx.state](ctx, event) ?? unhandled;
}

export function checkpointOf(ctx: LessonContext): Checkpoint {
  return {
    sessionId: ctx.sessionId,
    state: ctx.state,
    mode: ctx.mode,
    turnIndex: ctx.turnIndex,
    turns: ctx.turns,
    degradeReason: ctx.degradeReason,
  };
}

/** Rehydrates a session from a server checkpoint so an app kill loses nothing. */
export function restore(checkpoint: Checkpoint, totalTurns: number): LessonContext {
  return {
    sessionId: checkpoint.sessionId,
    // A session restored mid-turn resumes at the start of that turn rather than
    // mid-recording, which would be unrecoverable.
    state: resumeStateFor(checkpoint.state, checkpoint.mode),
    mode: checkpoint.mode,
    turnIndex: checkpoint.turnIndex,
    totalTurns,
    turns: checkpoint.turns,
    prepareAttempts: 0,
    degradeReason: checkpoint.degradeReason,
    pauseReason: null,
    resumeState: null,
    audioRebuilt: false,
    completedAt: checkpoint.state === 'complete' ? Date.now() : null,
  };
}

function resumeStateFor(state: LessonState, mode: SessionMode): LessonState {
  if (state === 'complete' || state === 'abandoned') return state;
  if (state === 'degraded') return 'degraded';
  if (state === 'completing') return 'completing';
  return mode === 'text' ? 'awaitingUser' : 'preparing';
}

/** Progress for the turn pips in the UI. */
export function progress(ctx: LessonContext): { current: number; total: number; fraction: number } {
  const current = Math.min(ctx.turnIndex + 1, ctx.totalTurns);
  return {
    current,
    total: ctx.totalTurns,
    fraction: ctx.totalTurns === 0 ? 0 : current / ctx.totalTurns,
  };
}
